import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { GridApi } from 'ag-grid-community/dist/lib/gridApi';
import { Button, DataList, Modal, ModalBody, ModalFooter, ModalHeader } from 'common/components';
import { ACTION_ITEMS } from 'common/config';
import { ButtonVariant } from 'common/enums';
import { FieldError } from 'common/models';

import AdminRoleForm from '../admin-role-form/AdminRoleForm';
import {
    CreateRoleDto,
    EditRoleDto,
    FeatureAccessWithOptionalSubfeatures,
    RoleFeature
} from 'modules/admin-roles-management/models/role-management.model';
import {
    addNewRole,
    getAllApplicationFeatures,
    updateRole
} from 'modules/admin-roles-management/utils/role-management.utils';
import { useDispatch } from 'react-redux';
import { addValidationError, removeValidationError } from 'common/utils/error';
import {
    AccessActions,
    FeatureActions,
    RoleField
} from 'modules/admin-roles-management/enums/admin-role.enum';
import {
    DESCRIPTION_MAX_LENGTH,
    DESCRIPTION_MAX_LENGTH_ERROR,
    ROLE_NAME_EMPTY_ERROR,
    ROLE_NAME_INVALID_CHAR_ERROR,
    ROLE_NAME_MAX_LENGTH,
    ROLE_NAME_MAX_LENGTH_ERROR,
    getFeaturesHeaders
} from 'modules/admin-roles-management/config';
import { validateMaxLength } from 'common/utils/validator';
import { EMPTY_GUID } from 'common/constants/user';
import { onAction } from 'modules/admin-roles-management/utils/role-management-features.utils';
import useAdminRoleManagementAccess from 'modules/admin-roles-management/role-access-hooks/useAdminRoleManagementAccess';
import './AdminRoleAddEdit.scss';
type AdminRoleAddEditProps = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    isVisible: boolean;
    onClose: () => void;
    onSuccess: () => void;
};
const AdminRoleAddEdit: FC<AdminRoleAddEditProps> = ({ data, isVisible, onClose, onSuccess }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Array<FieldError>>([]);
    const [featuresData, setAllFeaturesData] = useState<FeatureAccessWithOptionalSubfeatures[]>([]);
    const { isAdminRoleActionAccessDisabled } = useAdminRoleManagementAccess();
    const backupData = useRef<RoleFeature[]>([]);
    const flattenedData = useRef<FeatureAccessWithOptionalSubfeatures[]>([]);
    const dispatch = useDispatch();

    // const featuresSelected = useRef<FeatureAccessWithOptionalSubfeatures[]>([]);
    useEffect(() => {
        if (featuresData.length === 0) {
            getAllApplicationFeatures(
                data ? data.applicationRoleId : EMPTY_GUID,
                dispatch,
                setAllFeaturesData,
                backupData,
                flattenedData
            );
        }
        return () => {
            setAllFeaturesData([]);
            backupData.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        let errors = removeValidationError(fieldErrors, RoleField.Name);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);

        if (event.target.value.trim().length === 0) {
            errors = addValidationError(errors, RoleField.Name, ROLE_NAME_EMPTY_ERROR);
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(errors, RoleField.Name, ROLE_NAME_INVALID_CHAR_ERROR);
        }
        if (validateMaxLength(event.target.value, ROLE_NAME_MAX_LENGTH)) {
            errors = addValidationError(errors, RoleField.Name, ROLE_NAME_MAX_LENGTH_ERROR);
        }
        setName(event.target.value);
        setFieldErrors(errors);
    };

    const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        let errors = removeValidationError(fieldErrors, RoleField.Description);
        if (validateMaxLength(event.target.value, DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                RoleField.Description,
                DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        setDescription(event.target.value);
        setFieldErrors(errors);
    };

    const handleUpdateRole = () => {
        let errors = fieldErrors;
        if (!name.trim()) {
            errors = addValidationError(errors, RoleField.Name, ROLE_NAME_EMPTY_ERROR);
        }
        if (
            flattenedData.current.some((ele) => {
                return ele.writeAccess && !ele.readAccess;
            })
        ) {
            errors = addValidationError(
                errors,
                RoleField.WRITE_WITHOUT_READ,
                'Write access cannot be given without read access'
            );
        }

        if (errors.length === 0) {
            if (data) {
                const updatedData: EditRoleDto = {
                    applicationRoleId: data.applicationRoleId,
                    applicationRoleName: name,
                    description: description,
                    featuresReadWriteDataSet: flattenedData.current.map((ele) => ({
                        applicationFeatureId: ele.applicationFeatureId,
                        readAccess: ele.readAccess,
                        writeAccess: ele.writeAccess,
                        roleFeatureMappingId: ele.roleFeatureMappingId
                    }))
                };
                updateRole(updatedData, dispatch, onSuccess);
                flattenedData.current = [];
            }
        }
        setFieldErrors(errors);
    };

    const handleAddNewRole = () => {
        let errors = fieldErrors;
        if (!name.trim()) {
            errors = addValidationError(errors, RoleField.Name, ROLE_NAME_EMPTY_ERROR);
        }
        if (
            flattenedData.current.some((ele) => {
                return ele.writeAccess && !ele.readAccess;
            })
        ) {
            errors = addValidationError(
                errors,
                RoleField.WRITE_WITHOUT_READ,
                'Write access cannot be given without read access'
            );
        }

        if (errors.length === 0) {
            const newRoleData: CreateRoleDto = {
                applicationRoleName: name,
                description: description,
                featuresReadWriteDataSet: flattenedData.current.map((ele) => ({
                    applicationFeatureId: ele.applicationFeatureId,
                    readAccess: ele.readAccess,
                    writeAccess: ele.writeAccess,
                    roleFeatureMappingId: ele.roleFeatureMappingId
                }))
            };
            addNewRole(newRoleData, dispatch, onSuccess);
            flattenedData.current = [];
        }
        setFieldErrors(errors);
    };

    const headerTitle = data ? 'Edit Role' : 'Add Role';
    const handleAction = (
        value: boolean,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any,
        action: FeatureActions,
        api: GridApi,
        rowIndex: number,
        actionName: AccessActions
    ) =>
        onAction(
            value,
            data,
            action,
            api,
            rowIndex,
            actionName,
            setAllFeaturesData,
            dispatch,
            flattenedData,
            setFieldErrors,
            fieldErrors
        );

    useEffect(() => {
        if (data) {
            setName(data.applicationRoleName);
            setDescription(data.description);
            flattenedData.current = [];
        }
        return () => {
            setName('');
            setDescription('');
            flattenedData.current = [];
        };
    }, [data]);
    return (
        <Modal
            show={isVisible}
            // style={{ width: '70vw', maxheight: 'unset', height: '95vh' }}
            className="custom-modal-height-and-width"
            size="xl"
            onClose={onClose}>
            <ModalHeader>{headerTitle}</ModalHeader>
            <ModalBody>
                <AdminRoleForm
                    name={name}
                    description={description}
                    fieldErrors={fieldErrors}
                    handleNameChange={handleNameChange}
                    handleDescriptionChange={handleDescriptionChange}
                />
                <div className="feature-table-container">
                    {isVisible && (
                        <DataList
                            isNoDataVisible={featuresData?.length === 0}
                            data={featuresData}
                            columnDefs={getFeaturesHeaders(
                                handleAction,
                                isAdminRoleActionAccessDisabled
                            )}
                            suppressDrag={true}
                        />
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    variant={ButtonVariant.Primary}
                    disabled={fieldErrors.length > 0}
                    onClick={data ? handleUpdateRole : handleAddNewRole}>
                    {ACTION_ITEMS.SAVE_BUTTON}
                </Button>
                <Button variant={ButtonVariant.Secondary} onClick={onClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AdminRoleAddEdit;
